import { TeamBestPracticesData } from "./buildTeamBestPracticesData";

interface PerformanceExample {
  interaction_id: string;
  score: number;
  summary: string;
  details?: any;
  score_justification: string;
  next_time?: string;
}

interface AttributePerformanceData {
  attribute_name: string;
  attribute_definition?: string;
  your_stats: {
    avg_score: number;
    min_score: number;
    max_score: number;
    median_score: number;
    stddev_score: number;
    scored_count: number;
    not_scored_count: number;
  };
  team_stats: {
    avg_score: number;
    min_score: number;
    max_score: number;
    median_score: number;
    stddev_score: number;
    scored_count: number;
    not_scored_count: number;
  };
  your_vs_team: {
    delta: number;
    percentage_of_team: number;
    percentage_point_diff: number;
  };
  best_performer: {
    name?: string;
    email: string;
    avg_score: number;
    delta: number;
    percentage_of_best: number;
    percentage_point_diff: number;
  };
  ranking: {
    rank: number;
    total_agents: number;
    percentile: number;
  };
  trend: {
    prev_avg_score: number;
    delta: number;
    percentage_of_prev: number;
    percentage_point_change: number;
  };
  low_scoring_examples: PerformanceExample[];
  high_scoring_examples: PerformanceExample[];
}

export interface AgentPerformanceAnalysisData {
  analysis_date: string;
  total_conversations: number;
  lens_name: string;
  total_attributes: number;
  attributes: AttributePerformanceData[];
}

const formatScore = (score: number | null): string => {
  if (score === null || score === undefined) {
    return "N/A";
  }
  return `${score.toFixed(2)} / 5.00`;
};

const formatDelta = (delta: number | null): string => {
  if (delta === null || delta === undefined) {
    return "N/A";
  }
  return delta >= 0 ? `+${delta.toFixed(2)}` : delta.toFixed(2);
};

const formatPercentageDelta = (delta: number | null): string => {
  if (delta === null || delta === undefined) {
    return "N/A";
  }
  return delta >= 0 ? `+${delta.toFixed(1)}%` : `${delta.toFixed(1)}%`;
};

const formatPercentage = (value: number | null): string => {
  if (value === null || value === undefined) {
    return "N/A";
  }
  return `${value.toFixed(1)}%`;
};

const formatOrdinal = (rank: number): string => {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = rank % 100;
  return rank + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
};

const formatStddev = (stddev: number | null): string => {
  if (stddev === null || stddev === undefined) {
    return "N/A";
  }
  return stddev.toFixed(2);
};

const renderExample = (example: PerformanceExample, index: number, includeNextTime: boolean): string => {
  const detailsSection = example.details ? `**Details**: ${JSON.stringify(example.details)}\n\n` : "";
  const nextTimeSection =
    includeNextTime && example.next_time ? `**What to Do Differently Next Time**: ${example.next_time}\n\n` : "";

  return `#### Example ${index + 1}: Score ${example.score}/5
**Interaction ID**: ${example.interaction_id}

**Summary**: ${example.summary}

${detailsSection}**Score Justification**: ${example.score_justification}

${nextTimeSection}---
`;
};

const renderExamples = (
  examples: PerformanceExample[],
  title: string,
  includeNextTime: boolean
): string => {
  if (examples.length === 0) {
    return "";
  }

  const examplesList = examples.map((ex, idx) => renderExample(ex, idx, includeNextTime)).join("\n");
  return `### ${title}

${examplesList}`;
};

const renderAttributeDefinition = (definition?: string): string => {
  if (!definition) {
    return "";
  }
  return `### What This Attribute Measures
${definition}
`;
};

const renderAttribute = (attr: AttributePerformanceData, index: number): string => {
  const bestPerformerLabel = attr.best_performer.name
    ? `${attr.best_performer.name} (${attr.best_performer.email})`
    : attr.best_performer.email;

  return `## Attribute ${index + 1}: ${attr.attribute_name}

### Performance Statistics
- **Your Average Score**: ${formatScore(attr.your_stats.avg_score)}
- **Minimum Score**: ${attr.your_stats.min_score}
- **Maximum Score**: ${attr.your_stats.max_score}
- **Median Score**: ${attr.your_stats.median_score}
- **Standard Deviation**: ${formatStddev(attr.your_stats.stddev_score)}
- **Conversations Scored**: ${attr.your_stats.scored_count}
- **Conversations Not Scored**: ${attr.your_stats.not_scored_count}

### Comparative Performance
- **Team Average Score**: ${formatScore(attr.team_stats.avg_score)}
- **Team Minimum Score**: ${attr.team_stats.min_score}
- **Team Maximum Score**: ${attr.team_stats.max_score}
- **Team Median Score**: ${attr.team_stats.median_score}
- **Team Standard Deviation**: ${formatStddev(attr.team_stats.stddev_score)}
- **Team Total Conversations Scored**: ${attr.team_stats.scored_count}
- **Team Total Conversations Not Scored**: ${attr.team_stats.not_scored_count}

### Your Performance vs Team
- **Difference from Team Average**: ${formatDelta(attr.your_vs_team.delta)}
- **Percentage of Team Average**: ${formatPercentage(attr.your_vs_team.percentage_of_team)}
- **Percentage Point Difference**: ${formatPercentageDelta(attr.your_vs_team.percentage_point_diff)}

### Your Performance vs Best Performer
- **Best Performer**: ${bestPerformerLabel}
- **Best Performer Average**: ${formatScore(attr.best_performer.avg_score)}
- **Your Score as Percentage of Best**: ${formatPercentage(attr.best_performer.percentage_of_best)}
- **Difference from Best**: ${formatDelta(attr.best_performer.delta)}
- **Percentage Point Difference from Best**: ${formatPercentageDelta(attr.best_performer.percentage_point_diff)}

### Your Ranking
- **Rank**: ${formatOrdinal(attr.ranking.rank)} out of ${attr.ranking.total_agents} agents
- **Percentile**: ${formatOrdinal(Math.round(attr.ranking.percentile * 100))} percentile

### Performance Trend
- **Previous Period Average**: ${formatScore(attr.trend.prev_avg_score)}
- **Change from Previous Period**: ${formatDelta(attr.trend.delta)}
- **Percentage of Previous Period**: ${formatPercentage(attr.trend.percentage_of_prev)}
- **Percentage Point Change**: ${formatPercentageDelta(attr.trend.percentage_point_change)}

${renderAttributeDefinition(attr.attribute_definition)}
${renderExamples(attr.low_scoring_examples, "Low-Scoring Examples", true)}
${renderExamples(attr.high_scoring_examples, "High-Scoring Examples", false)}`;
};

export const convertToAgentPerformanceAnalysis = (data: AgentPerformanceAnalysisData): string => {
  const attributeSections = data.attributes.map(renderAttribute).join("\n");

  return `# Performance Analysis
**Analysis Date**: ${data.analysis_date}
**Total Conversations Analyzed**: ${data.total_conversations}

---

${attributeSections}
*This analysis is based on ${data.total_conversations} conversations and uses the ${data.lens_name} lens to evaluate performance across ${data.total_attributes} key attributes.*`;
};

const formatJustificationLines = (justification: string): string => {
  const lines = justification.split("\n").filter((line) => line.trim().length > 0);
  const hasListMarkers = lines.some((line) => line.trim().startsWith("-") || line.trim().startsWith("•"));

  return hasListMarkers ? lines.join("\n") : lines.map((line) => `- ${line.trim()}`).join("\n");
};

const renderTeamExample = (
  example: TeamBestPracticesData["attributes"][0]["examples"][0],
  index: number
): string => {
  const agentLabel = example.agent_name
    ? `${example.agent_name} (${example.agent_email})`
    : example.agent_email;
  const detailsSection = example.details ? `**Details**:\n${JSON.stringify(example.details)}\n\n` : "";

  return `#### Example ${index + 1}: Score ${example.score}/5
**Agent**: ${agentLabel}
**Interaction ID**: ${example.interaction_id}

**Summary**:
${example.summary}

${detailsSection}**Score Justification**:
${formatJustificationLines(example.score_justification)}

---
`;
};

const renderTeamAttribute = (
  attribute: TeamBestPracticesData["attributes"][0],
  index: number
): string => {
  const examplesList = attribute.examples.map(renderTeamExample).join("\n");

  return `## Attribute ${index + 1}: ${attribute.attribute_name}

### Excellence Examples

${examplesList}`;
};

export const convertTeamBestPracticesToMarkdown = (data: TeamBestPracticesData): string => {
  const attributeSections = data.attributes.map(renderTeamAttribute).join("\n");

  return `# Team Best Practices: Excellence in ${data.lens_name}

**Analysis Period**: ${data.analysis_period}
**Total Team Conversations Analyzed**: ${data.total_conversations}
**Purpose**: ${data.purpose}

---

${attributeSections}`;
};
